import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, ChevronRight, Trash2 } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { NewShowDialog } from "@/components/NewShowDialog";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ShowCard = ({ show, index, isAdmin, onDelete }) => {
  const done = show.vehicles.filter((v) => v.finalized_at).length;
  const total = show.vehicles.length;
  const status = done === total ? ["Concluído", "text-[#00E676] border-[#00E676]/50"] : done > 0 || show.vehicles.some((v) => v.checked) ? ["Em andamento", "text-[#FFB300] border-[#FFB300]/50"] : ["Pendente", "text-slate-400 border-slate-600"];
  return (
    <li className="fade-up" style={{ animationDelay: `${index * 60}ms` }}>
      <div className="relative rounded-xl border border-[#2A2E39] bg-[#15181E] hover:bg-[#1C2029] hover:border-[#636b2f] transition-colors">
        <Link to={`/show/${show.id}`} className="flex items-center gap-4 p-4 pr-14" data-testid={`show-card-${show.id}`}>
          <div className="text-center shrink-0 w-14">
            <div className="font-display text-3xl font-black text-white leading-none">{fmtDate(show.date).slice(0, 2)}</div>
            <div className="font-mono text-[10px] uppercase text-slate-500">{fmtDate(show.date).slice(3)}</div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-xl font-bold uppercase tracking-wide text-white truncate">{show.city}</div>
            {show.venue && <div className="text-xs text-slate-400 flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{show.venue}</div>}
            <div className="flex items-center gap-2 mt-2">
              <span className={`font-mono text-[10px] uppercase tracking-widest border rounded px-1.5 py-0.5 ${status[1]}`}>{status[0]}</span>
              <span className="font-mono text-xs text-slate-400" data-testid={`show-progress-${show.id}`}>{done}/{total} veículos</span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-500" />
        </Link>
        {isAdmin && (
          <button onClick={() => onDelete(show)} data-testid={`show-delete-btn-${show.id}`} className="absolute top-2 right-2 tap-target grid place-items-center rounded-md text-slate-600 hover:text-[#FF3838] transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </li>
  );
};

export default function ShowsPage() {
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const [shows, setShows] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/shows").then((r) => setShows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    try {
      const r = await api.post("/shows", form);
      toast.success("Checklist criado");
      navigate(`/show/${r.data.id}`);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const remove = async () => {
    try { await api.delete(`/shows/${toDelete.id}`); toast.success("Show removido"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#A9B26A]">Carregamentos</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-white leading-none mt-1">Shows</h1>
          </div>
          <NewShowDialog onCreate={create} />
        </div>
        {shows === null ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-xl shimmer" />)}</div>
        ) : shows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#2A2E39] p-10 text-center text-slate-400" data-testid="shows-empty">
            Nenhum carregamento ainda. Crie o primeiro show para começar o checklist.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="shows-list">
            {shows.map((s, i) => <ShowCard key={s.id} show={s} index={i} isAdmin={me.role === "admin"} onDelete={setToDelete} />)}
          </ul>
        )}
      </main>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#15181E] border-[#2A2E39] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {toDelete?.city}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Todo o checklist e as fotos deste show serão removidos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2A2E39] text-slate-300 hover:bg-[#1C2029] hover:text-white" data-testid="show-delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#FF3838] hover:bg-red-500 text-white" data-testid="show-delete-confirm-btn">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
